import { useState } from "react";

import { Button, StatusMessage } from "@ceedero/ui";

import { useCompleteRecovery, useRequestRecovery } from "@/account/hooks/use-account";
import { SUPPORT_EMAIL } from "@/account/utils/support";
import { resolveRecoveryErrorKey } from "@/account/utils/account-refusal";
import {
  validateNewPassword,
  validatePasswordConfirmation,
  validatePurchaseEmail,
  validateRecoveryCode,
} from "@/account/utils/credential-validation";
import { FormField } from "@/components/form-field";
import { useTranslation } from "@/localization/hooks/use-translation";

import type { TranslationKey } from "@/localization/hooks/use-translation";

interface CompletionErrors {
  code?: TranslationKey | undefined;
  confirmation?: TranslationKey | undefined;
  newPassword?: TranslationKey | undefined;
}

const EMPTY_COMPLETION = { code: "", confirmation: "", newPassword: "" };

/**
 * Getting back into an account whose password is forgotten, in the place a
 * sign-in was just refused.
 *
 * Two steps, because the proof is an inbox: the purchase email is asked for, a
 * code is mailed to it, and the code is what a new password is set against. The
 * mail comes from Polar rather than from Ceedero — it is Polar's own sign-in
 * code — so the form says whose email to look for, or the one a customer is
 * waiting on reads as a stranger's.
 *
 * Asking for a code says the same thing whoever the address belongs to. The API
 * will not say whether an address bought Ceedero, so neither may this.
 */
export const RecoveryForm = ({ onDone }: { onDone: (username?: string) => void }) => {
  const { t } = useTranslation();
  const requestRecovery = useRequestRecovery();
  const completeRecovery = useCompleteRecovery();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<TranslationKey | undefined>(undefined);
  const [completion, setCompletion] = useState(EMPTY_COMPLETION);
  const [completionErrors, setCompletionErrors] = useState<CompletionErrors>({});

  const requestErrorKey = resolveRecoveryErrorKey(requestRecovery.error);
  const completionErrorKey = resolveRecoveryErrorKey(completeRecovery.error);

  const submitEmail = (event: React.SyntheticEvent) => {
    event.preventDefault();
    const error = validatePurchaseEmail(email);
    setEmailError(error);
    if (error !== undefined) {
      return;
    }
    requestRecovery.mutate(email.trim());
  };

  const submitCompletion = (event: React.SyntheticEvent) => {
    event.preventDefault();
    const errors: CompletionErrors = {
      code: validateRecoveryCode(completion.code),
      confirmation: validatePasswordConfirmation({
        confirmation: completion.confirmation,
        password: completion.newPassword,
      }),
      newPassword: validateNewPassword(completion.newPassword),
    };
    setCompletionErrors(errors);
    if (Object.values(errors).some((error) => error !== undefined)) {
      return;
    }
    completeRecovery.mutate({
      code: completion.code.trim(),
      newPassword: completion.newPassword,
    });
  };

  const update =
    (field: keyof typeof EMPTY_COMPLETION) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setCompletion((current) => ({ ...current, [field]: value }));
      setCompletionErrors((errors) => ({ ...errors, [field]: undefined }));
      completeRecovery.reset();
    };

  const startOver = () => {
    requestRecovery.reset();
    completeRecovery.reset();
    setCompletion(EMPTY_COMPLETION);
    setCompletionErrors({});
  };

  if (completeRecovery.isSuccess) {
    const { username } = completeRecovery.data;
    return (
      <div className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold">{t("account.recovery.doneTitle")}</h2>
        <StatusMessage tone="success">{t("account.recovery.done", { username })}</StatusMessage>
        <p className="text-sm text-muted-foreground">{t("account.recovery.doneDescription")}</p>
        <Button type="button" onClick={() => onDone(username)}>
          {t("account.signIn.submit")}
        </Button>
      </div>
    );
  }

  if (requestRecovery.isSuccess) {
    return (
      <form onSubmit={submitCompletion} noValidate className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <h2 className="text-lg font-semibold">{t("account.recovery.title")}</h2>
          <p className="text-sm text-muted-foreground">{t("account.recovery.sent")}</p>
        </div>

        <FormField
          label={t("account.recovery.code")}
          value={completion.code}
          onChange={update("code")}
          autoComplete="one-time-code"
          autoCapitalize="characters"
          spellCheck={false}
          errorMessage={completionErrors.code === undefined ? undefined : t(completionErrors.code)}
        />
        <FormField
          label={t("account.password.new")}
          type="password"
          value={completion.newPassword}
          onChange={update("newPassword")}
          hint={t("account.password.hint")}
          autoComplete="new-password"
          errorMessage={
            completionErrors.newPassword === undefined ? undefined : t(completionErrors.newPassword)
          }
        />
        <FormField
          label={t("account.password.confirm")}
          type="password"
          value={completion.confirmation}
          onChange={update("confirmation")}
          autoComplete="new-password"
          errorMessage={
            completionErrors.confirmation === undefined
              ? undefined
              : t(completionErrors.confirmation)
          }
        />

        {completionErrorKey !== undefined && (
          <StatusMessage tone="error">{t(completionErrorKey)}</StatusMessage>
        )}

        <Button type="submit" isLoading={completeRecovery.isPending}>
          {t("account.recovery.submit")}
        </Button>
        <div className="flex flex-wrap justify-between gap-2">
          <Button type="button" variant="link" onClick={startOver}>
            {t("account.recovery.resend")}
          </Button>
          <Button type="button" variant="link" onClick={() => onDone()}>
            {t("account.recovery.back")}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={submitEmail} noValidate className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <h2 className="text-lg font-semibold">{t("account.recovery.title")}</h2>
        <p className="text-sm text-muted-foreground">{t("account.recovery.description")}</p>
      </div>

      <FormField
        label={t("account.recovery.email")}
        type="email"
        value={email}
        onChange={(event) => {
          setEmail(event.target.value);
          setEmailError(undefined);
          requestRecovery.reset();
        }}
        autoComplete="email"
        autoCapitalize="none"
        spellCheck={false}
        hint={t("account.recovery.noInbox", { email: SUPPORT_EMAIL })}
        errorMessage={emailError === undefined ? undefined : t(emailError)}
      />

      {requestErrorKey !== undefined && (
        <StatusMessage tone="error">{t(requestErrorKey)}</StatusMessage>
      )}

      <Button type="submit" isLoading={requestRecovery.isPending}>
        {t("account.recovery.send")}
      </Button>
      <Button type="button" variant="link" onClick={() => onDone()}>
        {t("account.recovery.back")}
      </Button>
    </form>
  );
};
